import { useMsal } from "@azure/msal-react";
import { InteractionRequiredAuthError } from "@azure/msal-browser";
import { loginRequest, apiRequest, graphConfig } from "../config/authConfig";
import { useCallback } from "react";
import { saveRememberMe, clearRememberMe } from "../utils/rememberMe";

/**
 * Custom hook to handle login
 */
export const useLogin = () => {
    const { instance } = useMsal();

    const loginPopup = (rememberMe = false) => {
        return instance.loginPopup(loginRequest).then((response) => {
            if (rememberMe && response.account) {
                // Save the username for auto-login
                saveRememberMe(response.account.username);
            }
            return response;
        });
    };

    const loginRedirect = (rememberMe = false) => {
        if (rememberMe) {
            // For redirect, we'll store a flag and handle it after redirect
            sessionStorage.setItem('pendingRememberMe', 'true');
        }
        return instance.loginRedirect(loginRequest);
    };

    return { loginPopup, loginRedirect };
};

/** 
 * Custom hook to handle logout
 */
export const useLogout = () => {
    const { instance } = useMsal();

    const logout = () => {
        // Clear remember me when user explicitly logs out
        clearRememberMe();
        return instance.logoutPopup();
    };

    const logoutRedirect = () => {
        // Clear remember me when user explicitly logs out
        clearRememberMe();
        return instance.logoutRedirect();
    };

    return { logout, logoutRedirect };
};

/**
 * Custom hook to get access token
 */
export const useAccessToken = () => {
    const { instance, accounts } = useMsal();

    const getAccessToken = useCallback(async (scopes = ["User.Read"]) => {
        const request = {
            scopes: scopes,
            account: accounts[0]
        };

        try {
            const response = await instance.acquireTokenSilent(request);
            return response.accessToken;
        } catch (error) {
            if (error instanceof InteractionRequiredAuthError) {
                // fallback to interaction when silent call fails
                try {
                    const response = await instance.acquireTokenPopup(request);
                    return response.accessToken;
                } catch (popupError) {
                    throw popupError;
                }
            } else {
                throw error;
            }
        }
    }, [instance, accounts]);

    // Convenience method to get token for backend API
    const getBackendAccessToken = useCallback(async () => {
        try {
            return await getAccessToken(apiRequest.scopes);
        } catch (error) {
            throw error;
        }
    }, [getAccessToken]);

    return { getAccessToken, getBackendAccessToken };
};

/**
 * Utility function to call Microsoft Graph API
 */
export const callMsGraph = async (accessToken) => {
    const headers = new Headers();
    const bearer = `Bearer ${accessToken}`;

    headers.append("Authorization", bearer);

    const options = {
        method: "GET",
        headers: headers
    };

    try {
        const response = await fetch(graphConfig.graphMeEndpoint, options);
        return response.json();
    } catch (error) {
        throw error;
    }
};

/**
 * Utility function to make authenticated API calls to the backend
 */
export const callApi = async (accessToken, url, options = {}) => {
    const headers = new Headers();
    const bearer = `Bearer ${accessToken}`;

    headers.append("Authorization", bearer);
    headers.append("Content-Type", "application/json");

    const requestOptions = {
        method: "GET",
        headers: headers,
        ...options
    };

    try {
        const response = await fetch(url, requestOptions);
        
        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }
        
        const contentType = response.headers.get("content-type");
        if (contentType && contentType.indexOf("application/json") !== -1) {
            return response.json();
        } else {
            return response.text();
        }
    } catch (error) {
        throw error;
    }
};
